import { useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from 'tailwind-v4/components/badge'
import { Input } from 'tailwind-v4/components/input'
import { cn } from 'tailwind-v4/lib/utils'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface RecipientContact {
  id?: string | null
  email: string
  name?: string
}

interface PassedProps {
  contacts: RecipientContact[]
  onAdd: (email: string) => void
  onRemove: (contact: RecipientContact) => void
}

/**
 * v4 recipient input: existing contacts render as removable badge chips with a
 * free-text email field inline. Enter / comma commits a valid email.
 */
export function EmailRecipients({ contacts, onAdd, onRemove }: PassedProps) {
  const [value, setValue] = useState('')

  const commit = () => {
    const email = value.trim().replace(/,$/, '')
    if (!email || !EMAIL_RE.test(email)) return
    onAdd(email)
    setValue('')
  }

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      {contacts.map((contact) => (
        <Badge
          key={contact.id ?? contact.email}
          variant="secondary"
          className="gap-1 pr-1"
        >
          <span className="truncate">{contact.email}</span>
          <button
            type="button"
            aria-label={`Remove ${contact.email}`}
            onClick={() => {
              onRemove(contact)
            }}
            className="rounded-full p-0.5 opacity-60 transition-opacity hover:opacity-100"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <Input
        type="email"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            commit()
          }
        }}
        onBlur={commit}
        placeholder="Add email…"
        className={cn(
          'h-7 w-32 min-w-0 flex-1 border-0 bg-transparent px-1 shadow-none',
          'focus-visible:border-0 focus-visible:ring-0'
        )}
      />
    </div>
  )
}

export default EmailRecipients
